import {
  ClientFragment,
  useClientsQuery,
  useDeleteClientMutation,
} from "@graphql/clients/clients.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  ClientsTableConfig,
  renderCreatedAt,
  renderDescription,
  renderDomain,
  renderEmail,
  renderName,
  renderUpdatedAt,
} from "./ClientsTableConfig";
import { ClientTableColumns } from "./UI/ClientsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

const columnHelper = createColumnHelper<ClientFragment>();

type Props = {
  initialPageIndex?: number;
  nameSearch?: string;
  domainSearch?: string;
  emailSearch?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
};

export const ClientsTable = ({
  initialPageIndex = 0,
  nameSearch,
  domainSearch,
  emailSearch,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = PAGE_SIZE,
}: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedNameSearch = useDebounce(nameSearch, SEARCH_DEBOUNCE_TIME);
  const debouncedDomainSearch = useDebounce(domainSearch, SEARCH_DEBOUNCE_TIME);
  const debouncedEmailSearch = useDebounce(emailSearch, SEARCH_DEBOUNCE_TIME);

  const clientsQuery = useClientsQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    name_Icontains: debouncedNameSearch || undefined,
    domain_Icontains: debouncedDomainSearch || undefined,
    email_Icontains: debouncedEmailSearch || undefined,
    sortBy: ConfigurableTableSystem.generateSortParameter(
      ClientsTableConfig.columns,
      sorting,
    ),
  });
  const deleteClientMutation = useDeleteClientMutation();
  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );
  const deleteClient = deleteClientMutation.mutateAsync;
  const refetchClients = clientsQuery.refetch;
  const isClientsLoading = clientsQuery.isLoading;
  const isClientsFetching = clientsQuery.isFetching;

  const totalCount = clientsQuery.data?.clients?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [
    debouncedNameSearch,
    debouncedDomainSearch,
    debouncedEmailSearch,
    pageSize,
  ]);

  const data = useMemo(() => {
    return (
      clientsQuery.data?.clients?.edges
        .map((edge) => edge?.node)
        .filter((client) => !!client) ?? []
    );
  }, [clientsQuery.data]);

  const handleDetailsClick = useCallback(
    (clientId: string) => {
      navigate(
        routes.portal.clients.client.dashboard.$buildPath({
          params: { clientId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleEditClick = useCallback(
    (clientId: string) => {
      navigate(
        routes.portal.clients.client.record.$buildPath({
          params: { clientId },
        }),
      );
    },
    [navigate],
  );

  const handleDeleteClick = useCallback(
    async (clientId: string) => {
      await deleteClient({ clientId });
      refetchClients();
    },
    [deleteClient, refetchClients],
  );

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...ClientsTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientsTableConfig.columns).map(([key, config]) => [
          key,
          {
            ...config,
            render: (value: unknown, row: ClientFragment) => {
              const columnKey = key as ClientTableColumns;

              // Map to the appropriate render function
              switch (columnKey) {
                case ClientTableColumns.name:
                  return renderName(value, row, handleDetailsClick);
                case ClientTableColumns.domain:
                  return renderDomain(value);
                case ClientTableColumns.email:
                  return renderEmail(value);
                case ClientTableColumns.createdAt:
                  return renderCreatedAt(value);
                case ClientTableColumns.updatedAt:
                  return renderUpdatedAt(value);
                case ClientTableColumns.description:
                  return renderDescription(value);
                default:
                  return String(value);
              }
            },
          },
        ]),
      ) as typeof ClientsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings, handleDetailsClick]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<ClientFragment, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2} justify="center" align="center">
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleDetailsClick(props.row.original.id)}
                />
                <Dropdown.Option
                  label="Edit"
                  icon="faPencil"
                  onSelect={() => handleEditClick(props.row.original.id)}
                />
                <Dropdown.Option
                  label="Delete"
                  icon="faTrash"
                  onSelect={() => handleDeleteClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleDetailsClick,
    handleEditClick,
    handleDeleteClick,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table<ClientFragment, string>
      columns={allColumnsWithActions as ColumnDef<ClientFragment, string>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isClientsLoading}
      isFetching={isClientsFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
      showFooter={showFooter}
      showHeader={showHeader}
      showPageSizeDropdown={showPageSizeDropdown}
      columnVisibility={columnSettings}
    />
  );
};
